/* eslint-disable no-empty-pattern */
import Vue from 'vue';
import {Project} from '../models/project';
import ErrorCodes from '@/helpers/errorCodes';
import {sortBy} from '../../helpers/utils';

export const ProjectType = Object.freeze({
   PRODUCT: 'Product',
   SW_NON_INTERNAL: 'Software - Non-internal use',
   SW_INTERNAL: 'Software - Internal use',
   SW_INTERNAL_EXEMPT: 'Software - Internal use, exempt',
   SW_DUAL: 'Software - Dual function',
   PROCESS: 'Process',
   OTHER: 'All Others',
});

const state = () => ({
   projects: {},
});

const getters = {
   projects: (state) => Object.values(state.projects).sort(sortBy('name')),

   projectMap: (state) => state.projects,

   /** Projects associated with study periods in the active study */
   projectsInStudy: (state, getters, rootState, rootGetters) => {
      const activeStudyPeriods = rootGetters['companies/activeStudyPeriods'];
      return getters.projects.filter((project) => {
         return project.periods.some((period) => activeStudyPeriods.includes(period));
      });
   },

   // TODO: Remove when unconfigured project assignment is supported
   // Skip if there is no assigned project.
   projectsSupportedPeriodIds: (state, getters) => {
      const periodIds = getters.projectsInStudy.flatMap((item) => item.periodIds) || [];
      return [...new Set(periodIds)];
   },

   /** Returns a method that returns projects that overlap a given year */
   projectsInPeriod: (state, getters) => {
      return (periodId) => {
         return getters.projects.filter((project) => {
            return project.periodIds.includes(+periodId);
         });
      };
   },
};

const mutations = {
   // Clear the projects state
   clear: (state) => {
      state.projects = {};
   },

   // Set the projects in the state
   setProjects: (state, {projects}) => {
      projects.forEach((project) => {
         Vue.set(state.projects, project.id, project);
      });
   },

   // Update or add a project
   updateProject: (state, {project}) => {
      Vue.set(state.projects, project.id, project);
   },

   deleteProject: (state, {projectId}) => {
      if (projectId in state.projects) {
         Vue.delete(state.projects, projectId);
      }
   },
};

const actions = {
   // Create a project
   async createProject({commit}, {project}) {
      const projData = (
         await this._vm.$http.post('/api/project', project.export(), {params: {force: true}})
      ).data;

      commit('updateProject', {project: new Project(projData)});
   },

   // Edit a project
   async editProject({commit}, {project}) {
      let projData;

      projData = (
         await this._vm.$http.put(`/api/project/${project.id}`, project.export(), {
            params: {force: true},
         })
      ).data;

      commit('updateProject', {project: new Project(projData)});
   },

   /** Load a single project */
   async loadProject({commit}, {projectId}) {
      const projectData = (
         await this._vm.$http.get(`/api/project/${projectId}`, {params: {force: true}})
      ).data;
      const project = new Project(projectData);
      commit('setProjects', {projects: [project]});
   },

   /** Load all projects for a company */
   async loadProjects({commit, getters}, {companyId}) {
      const projData = (await this._vm.$http.get(`/api/company/${companyId}/project`)).data.results;
      const projects = projData.map((data) => new Project(data));
      commit('clear');
      commit('setProjects', {projects});
      return getters.projects;
   },

   /** Delete a project */
   async deleteProject({commit}, {projectId, force = true}) {
      const params = {force};
      try {
         await this._vm.$http.delete(`/api/project/${projectId}`, {params});
      } catch (err) {
         const errCode = err.response.data.errors[0].code;
         if (errCode === ErrorCodes.CONFIRMATION_REQUIRED) {
            return err.response.data.errors[0].detail;
         }
         throw err;
      }
      commit('deleteProject', {projectId});
   },

   async inspectProject({}, {projectDescription}) {
      return (
         await this._vm.$http.post('/api/project/inspect', {
            projectDescription,
         })
      ).data;
   },

   async startProjectEvaluation({}, {companyId}) {
      return (
         await this._vm.$http.post('/api/project/evaluation', {
            companyId,
         })
      ).data;
   },

   async sendProjectEvaluationMessage({}, {chatId, content}) {
      return (await this._vm.$http.post(`/api/project/evaluation/${chatId}/chat`, {content})).data;
   },

   async summarizeProjectEvaluation({}, {chatId}) {
      return (await this._vm.$http.post(`/api/project/evaluation/${chatId}/summarize`, {})).data;
   },

   async createProjectFromEvaluation(
      {},
      {companyId, projectName, projectType, projectEvaluationId, speriodIds}
   ) {
      return (
         await this._vm.$http.post(
            '/api/project',
            {
               project: {
                  name: projectName,
                  ptype: projectType,
                  companyId: companyId,
                  projectEvaluationId: projectEvaluationId,
               },
               speriodIds: speriodIds,
            },
            {params: {force: true}}
         )
      ).data;
   },
};

export default {
   namespaced: true,
   state,
   getters,
   mutations,
   actions,
};
